import { useState } from 'react'
import { Brain, ChevronDown } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Shimmer } from '@/components/ai/shimmer'
import { agentDisplayName } from '@/config/env'
import { useNow } from '@/hooks/useNow'
import {
  convertToolName,
  getToolAction,
  getToolDetail,
  TOOL_ACTIONS,
} from '@/utils/toolConfig'

import { AgentAvatar } from '../AgentAvatar'
import type { WorkBlockDisplay, WorkStep } from '../groupEvents'
import {
  TOOL_CHEVRON_CLASSNAME,
  TOOL_ICON_CLASSNAME,
  WORK_NEST_CLASSNAME,
} from '../toolTriggerStyles'
import {
  elapsedLabel,
  isInFlight,
  latestThought,
  thoughtFirstLine,
} from './utils'
import { WorkSteps } from './WorkSteps'

function CurrentStep({ step }: { step: WorkStep | undefined }) {
  const call =
    step?.type === 'tool_call_group'
      ? step.toolCalls.at(-1)
      : step?.type === 'tool_call'
        ? step
        : undefined
  if (!call)
    return (
      <>
        <Brain aria-hidden="true" className={TOOL_ICON_CLASSNAME} />
        <Shimmer duration={1}>Thinking</Shimmer>
      </>
    )
  const Icon = TOOL_ACTIONS[getToolAction(call.toolName)].icon
  return (
    <>
      <Icon aria-hidden="true" className={TOOL_ICON_CLASSNAME} />
      <span className="min-w-0 truncate">
        <Shimmer duration={1}>
          {convertToolName(call.toolName, !isInFlight(call))}
        </Shimmer>{' '}
        <span className="font-normal text-muted-foreground">
          {getToolDetail(call.toolName, call.args)}
        </span>
      </span>
    </>
  )
}

// Owns the clock so the per-second tick re-renders this label, not the steps.
function ElapsedLabel({ startedAt }: { startedAt: Date }) {
  return elapsedLabel(startedAt, useNow())
}

export function LiveWorkBlock({ block }: { block: WorkBlockDisplay }) {
  const [open, setOpen] = useState(false)
  const thought = latestThought(block.steps)

  return (
    <Collapsible
      className="flex w-full flex-col items-start py-1"
      data-slot="work-block"
      onOpenChange={setOpen}
      open={open}>
      <span className="flex items-center gap-2">
        <AgentAvatar agentId="david" className="size-7" thinking />
        <span className="text-shimmer-gradient text-sm font-medium tabular-nums">
          {agentDisplayName('david')} is working ·{' '}
          <ElapsedLabel startedAt={block.startedAt} />
        </span>
      </span>
      <div className="flex w-full flex-col items-start pl-9">
        <CollapsibleTrigger className="group -ml-3 flex max-w-full cursor-pointer flex-col items-start gap-1 rounded-lg px-3 py-1.5 text-left text-sm font-medium leading-4 tracking-tight text-primary-high-contrast opacity-70 transition-opacity hover:opacity-100 data-[state=open]:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-david-solid/30">
          {thought && !open && (
            <span className="flex items-start gap-1.5 font-normal leading-normal text-muted-foreground">
              <Brain
                aria-hidden="true"
                className={`${TOOL_ICON_CLASSNAME} mt-0.5`}
              />
              <span className="line-clamp-4">
                {thoughtFirstLine(thought.content)}
              </span>
            </span>
          )}
          <span className="flex max-w-full items-center gap-1.5">
            <CurrentStep step={block.steps.at(-1)} />
            <ChevronDown
              aria-hidden="true"
              className={TOOL_CHEVRON_CLASSNAME}
            />
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className={WORK_NEST_CLASSNAME}>
          <WorkSteps live steps={block.steps} />
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
